import { useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { clientConfigAtom } from '@app/state/clientsAtom'
import { layersFormattedAtom } from '@app/domains/layers/state/layers.atoms'
import { flattenElementsFromCatalog } from '@app/domains/layers/utils/layers.utils'

export type RiskProfilePropertiesDomain = 'social' | 'economic'

export type RiskProfilePropertiesElementConfig = {
  element_ids: string[]
  property_lookup_key: string
}

/**
 * Resolves the full element IDs from the client config's
 * `risk_profile_properties` matched up with /layer
 */
export function useRiskProfilePropertiesElementConfig(
  domain: RiskProfilePropertiesDomain,
): RiskProfilePropertiesElementConfig | null {
  const clientConfig = useAtomValue(clientConfigAtom)
  const layers = useAtomValue(layersFormattedAtom)

  return useMemo(() => {
    const propertiesConfig = clientConfig?.risk_profile_properties
    if (!propertiesConfig) return null

    const numericIds =
      domain === 'social' ? propertiesConfig.social_elements : propertiesConfig.economic_elements

    const flattened = flattenElementsFromCatalog(layers.elements)
    const element_ids = numericIds
      .map((id) => {
        const prefix = `e${id}_`
        return flattened.find(
          ({ asset }) => typeof asset.type === 'string' && asset.type.startsWith(prefix),
        )?.asset.type as string | undefined
      })
      .filter((id): id is string => typeof id === 'string')

    return {
      element_ids,
      property_lookup_key: propertiesConfig.property_lookup_key,
    }
  }, [clientConfig, domain, layers.elements])
}
